import { useEffect, useMemo, useRef } from 'react';
import { useImmer } from 'use-immer';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useTranslation } from '@shared/core/i18n';
import {
  parseTimeInput,
  formatTime,
  generateTimeOptions,
  isTimeEqual,
  findClosestTimeOption,
  stepForward,
  stepBackward,
  isTimeInRange,
  filterTimeOptions,
  timeToInputValue,
  inputValueToTime,
} from './time.internal';
import type { Time, TimeOption, ResolvedTimeFormat } from './time.types.internal';
import type { TimePickerProps, TimePickerState } from './TimePicker.types';
import { ERROR_TIMEOUT_MS } from './TimePicker.styles.internal';

export interface UseTimePickerProps extends Pick<TimePickerProps, 'value' | 'onChange' | 'minuteStep' | 'min' | 'max' | 'clearable' | 'isDisabled' | 'isReadOnly'> {
  /**
   * The resolved time format ('12h' or '24h').
   * This should be pre-resolved from 'auto' before being passed to this hook.
   */
  format?: ResolvedTimeFormat;
  /**
   * BCP 47 language tag for locale-aware time formatting (e.g., 'en-US', 'zh-TW').
   * Used to display localized day-period markers (AM/PM, 上午/下午, etc.).
   * Defaults to 'en-US'.
   */
  locale?: string;
}

export interface UseTimePickerReturn {
  /** Current state of the time picker */
  state: TimePickerState;

  /** Refs for DOM elements */
  refs: {
    inputRef: React.RefObject<HTMLInputElement>;
    listRef: React.RefObject<HTMLDivElement>;
  };

  /** Event handlers for the time picker */
  handlers: {
    handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleInputBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
    handleInputKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
    handleSelectOption: (option: TimeOption) => void;
    handleClear: (e: React.MouseEvent) => void;
    handleOpenPopover: () => void;
    handleClosePopover: () => void;
    handleTogglePopover: () => void;
    handleNativeTimeChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleOpenAutoFocus: (e: Event) => void;
    setFocus: (isFocused: boolean) => void;
  };

  /** Computed values derived from props/state */
  computed: {
    timeOptions: TimeOption[];
    nativeInputValue: string;
    nativeMinValue: string;
    nativeMaxValue: string;
    nativeStepValue: number;
    /** The effective selection for visual highlighting (optimistic or prop value) */
    effectiveSelection: Time | undefined;
  };
}

interface InternalState extends TimePickerState {
  /** Optimistic selection for immediate visual feedback during arrow key navigation */
  optimisticValue: Time | undefined;
}

/**
 * Creates the initial state for the time picker, properly initializing
 * inputValue from the value prop to prevent flash of placeholder text.
 */
function createInitialState(value: Time | undefined, format: '12h' | '24h', locale: string): InternalState {
  return {
    isOpen: false,
    inputValue: value ? formatTime(value, format, locale) : '',
    errorMessage: null,
    isFocused: false,
    optimisticValue: undefined,
  };
}

export function useTimePicker({
  value,
  onChange,
  format = '12h',
  locale = 'en-US',
  minuteStep = 30,
  min,
  max,
  clearable = true,
  isDisabled = false,
  isReadOnly = false,
}: UseTimePickerProps): UseTimePickerReturn {
  const { tq } = useTranslation('timePicker');

  // Initialize state with value to prevent flash of placeholder
  const [state, setState] = useImmer<InternalState>(() => createInitialState(value, format, locale));
  const errorTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Generate and filter time options based on format, step, locale, and min/max constraints
  const timeOptions = useMemo(() => filterTimeOptions(generateTimeOptions(format, minuteStep, locale), min, max), [format, minuteStep, locale, min, max]);

  // Sync input value with external value changes
  useEffect(() => {
    if (value) {
      setState(draft => {
        draft.inputValue = formatTime(value, format, locale);
      });
    } else {
      setState(draft => {
        draft.inputValue = '';
      });
    }
  }, [value, format, locale, setState]);

  // Clear error timeout on unmount
  useEffect(() => {
    return () => {
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
      }
    };
  }, []);

  // Close popover when clicking outside the TimePicker (handles multiple pickers)
  useEffect(() => {
    if (!state.isOpen) return;

    const handleDocumentMouseDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const timePickerRoot = inputRef.current?.closest('[data-slot="time-picker"]');

      if (timePickerRoot && !timePickerRoot.contains(target)) {
        setState(draft => {
          draft.isOpen = false;
          draft.optimisticValue = undefined;
        });
      }
    };

    document.addEventListener('mousedown', handleDocumentMouseDown);
    return () => document.removeEventListener('mousedown', handleDocumentMouseDown);
  }, [state.isOpen, setState]);

  // Scroll to selected/closest option when opened
  useEffect(() => {
    if (state.isOpen && listRef.current && value) {
      const closest = findClosestTimeOption(value, timeOptions);
      if (closest) {
        const index = timeOptions.findIndex(opt => isTimeEqual(opt.value, closest.value));
        if (index !== -1) {
          // Small delay to ensure list is rendered
          requestAnimationFrame(() => {
            const buttons = listRef.current?.querySelectorAll('[data-time-option]');
            const targetButton = buttons?.[index];
            targetButton?.scrollIntoView({ block: 'center', behavior: 'instant' });
          });
        }
      }
    }
  }, [state.isOpen, value, timeOptions]);

  // Show error message with auto-dismiss
  const showError = useEventCallback((message: string): void => {
    setState(draft => {
      draft.errorMessage = message;
    });

    // Clear any existing timeout
    if (errorTimeoutRef.current) {
      clearTimeout(errorTimeoutRef.current);
    }

    // Auto-dismiss after timeout
    errorTimeoutRef.current = setTimeout(() => {
      setState(draft => {
        draft.errorMessage = null;
      });
    }, ERROR_TIMEOUT_MS);
  });

  // Input change handler
  const handleInputChange = useEventCallback((e: React.ChangeEvent<HTMLInputElement>): void => {
    setState(draft => {
      draft.inputValue = e.target.value;
      draft.errorMessage = null; // Clear error when user starts typing
    });
  });

  // Input blur handler - parse, validate, and close popover if focus moves outside
  const handleInputBlur = useEventCallback((e: React.FocusEvent<HTMLInputElement>): void => {
    // Read directly from the DOM element to ensure we have the latest value.
    // React state may be stale if the blur event fires before re-render completes.
    // Prefer inputRef (always available) over e.currentTarget (may be null in synthetic events).
    const inputValue = inputRef.current?.value ?? e.currentTarget?.value ?? '';

    // Check if focus is moving to an element inside the popover (e.g., time option)
    const relatedTarget = e.relatedTarget as HTMLElement | null;
    const isMovingToPopover = relatedTarget && listRef.current?.contains(relatedTarget);

    // Close the popover if focus is moving outside
    if (!isMovingToPopover) {
      setState(draft => {
        draft.isOpen = false;
        draft.optimisticValue = undefined;
      });
    }

    if (!inputValue.trim()) {
      // Empty input - clear value if clearable, otherwise restore previous
      if (clearable) {
        onChange?.(undefined);
      } else if (value) {
        setState(draft => {
          draft.inputValue = formatTime(value, format, locale);
        });
      }
      return;
    }

    const parsed = parseTimeInput(inputValue, format);

    if (parsed) {
      // Check if parsed time is within min/max range
      if (!isTimeInRange(parsed, min, max)) {
        showError(tq`Time is outside the allowed range`);
        if (value) {
          setState(draft => {
            draft.inputValue = formatTime(value, format, locale);
          });
        } else {
          setState(draft => {
            draft.inputValue = '';
          });
        }
        return;
      }
      // Valid input - update value and normalize display
      onChange?.(parsed);
      setState(draft => {
        draft.inputValue = formatTime(parsed, format, locale);
      });
    } else {
      // Invalid input - show error and revert
      showError(tq`Invalid time format`);
      if (value) {
        setState(draft => {
          draft.inputValue = formatTime(value, format, locale);
        });
      } else {
        setState(draft => {
          draft.inputValue = '';
        });
      }
    }
  });

  // Keyboard navigation handler
  const handleInputKeyDown = useEventCallback((e: React.KeyboardEvent<HTMLInputElement>): void => {
    // Don't process navigation keys when read-only or disabled
    if (isReadOnly || isDisabled) {
      return;
    }

    if (e.key === 'Enter') {
      // Close dropdown and blur input to confirm selection
      setState(draft => {
        draft.isOpen = false;
        draft.optimisticValue = undefined;
      });
      e.currentTarget.blur();
    } else if (e.key === 'Escape') {
      // Revert to last valid value and close
      setState(draft => {
        draft.inputValue = value ? formatTime(value, format, locale) : '';
        draft.isOpen = false;
        draft.optimisticValue = undefined;
      });
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      // Use optimistic value if set, otherwise use prop value
      const currentValue = state.optimisticValue ?? value;
      // Step forward to next valid time option (handles off-step values correctly)
      const newTime = stepForward(currentValue, minuteStep);
      // Check if new time is within range
      if (isTimeInRange(newTime, min, max)) {
        // Set optimistic value immediately for instant visual feedback
        setState(draft => {
          draft.optimisticValue = newTime;
          draft.inputValue = formatTime(newTime, format, locale);
        });
        onChange?.(newTime);
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      // Use optimistic value if set, otherwise use prop value
      const currentValue = state.optimisticValue ?? value;
      // Step backward to previous valid time option (handles off-step values correctly)
      const newTime = stepBackward(currentValue, minuteStep);
      // Check if new time is within range
      if (isTimeInRange(newTime, min, max)) {
        // Set optimistic value immediately for instant visual feedback
        setState(draft => {
          draft.optimisticValue = newTime;
          draft.inputValue = formatTime(newTime, format, locale);
        });
        onChange?.(newTime);
      }
    }
  });

  // Select option from dropdown
  const handleSelectOption = useEventCallback((option: TimeOption): void => {
    onChange?.(option.value);
    setState(draft => {
      draft.inputValue = option.label;
      draft.isOpen = false;
      draft.optimisticValue = undefined; // Clear optimistic value when selecting
    });
  });

  // Clear button handler
  const handleClear = useEventCallback((e: React.MouseEvent): void => {
    e.stopPropagation();
    onChange?.(undefined);
    setState(draft => {
      draft.inputValue = '';
      draft.optimisticValue = undefined;
    });
  });

  // Open popover
  const handleOpenPopover = useEventCallback((): void => {
    if (!isDisabled && !isReadOnly) {
      setState(draft => {
        draft.isOpen = true;
      });
    }
  });

  // Close popover
  const handleClosePopover = useEventCallback((): void => {
    setState(draft => {
      draft.isOpen = false;
      draft.optimisticValue = undefined; // Clear optimistic value when closing
    });
  });

  // Toggle popover open/closed
  const handleTogglePopover = useEventCallback((): void => {
    if (!isDisabled && !isReadOnly) {
      setState(draft => {
        draft.isOpen = !draft.isOpen;
        if (draft.isOpen === false) {
          draft.optimisticValue = undefined; // Clear optimistic value when closing
        }
      });
    }
  });

  // Prevent popover from stealing focus - keep it on input for arrow key navigation
  const handleOpenAutoFocus = useEventCallback((e: Event): void => {
    e.preventDefault();
    inputRef.current?.focus();
  });

  // Native time input change handler (mobile)
  const handleNativeTimeChange = useEventCallback((e: React.ChangeEvent<HTMLInputElement>): void => {
    const newTime = inputValueToTime(e.target.value);
    onChange?.(newTime);
  });

  // Set focus state
  const setFocus = useEventCallback((isFocused: boolean): void => {
    setState(draft => {
      draft.isFocused = isFocused;
    });
  });

  // Native input values for mobile
  const nativeInputValue = timeToInputValue(value);
  const nativeMinValue = timeToInputValue(min);
  const nativeMaxValue = timeToInputValue(max);
  const nativeStepValue = minuteStep * 60;

  // Effective selection for visual highlighting - prefer optimistic value for immediate feedback
  const effectiveSelection = state.optimisticValue ?? value;

  return {
    state: {
      isOpen: state.isOpen,
      inputValue: state.inputValue,
      errorMessage: state.errorMessage,
      isFocused: state.isFocused,
    },
    refs: {
      inputRef,
      listRef,
    },
    handlers: {
      handleInputChange,
      handleInputBlur,
      handleInputKeyDown,
      handleSelectOption,
      handleClear,
      handleOpenPopover,
      handleClosePopover,
      handleTogglePopover,
      handleNativeTimeChange,
      handleOpenAutoFocus,
      setFocus,
    },
    computed: {
      timeOptions,
      nativeInputValue,
      nativeMinValue,
      nativeMaxValue,
      nativeStepValue,
      effectiveSelection,
    },
  };
}
